import React, { useMemo, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { getDownloadUrlForPrivate } from "../api"; // <– justera sökväg vid behov

function fmtDate(d) {
  try {
    const date = typeof d === "string" ? new Date(d) : d;
    if (!date || isNaN(date)) return null;
    return date.toLocaleDateString(undefined, {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  } catch {
    return null;
  }
}

// Tar ut S3-key från din GleSYS-URL:
// https://objects.dc-sto1.glesys.net/dry-moon/uploads/editor/foo.pdf
//  → "uploads/editor/foo.pdf"
function extractKeyFromS3Url(href) {
  try {
    const url = new URL(href);
    const parts = url.pathname.replace(/^\/+/, "").split("/"); // ["dry-moon","uploads","editor","foo.pdf"]
    if (parts.length < 2) return null;
    const [, ...rest] = parts; // skippa "dry-moon"
    return rest.join("/");
  } catch {
    return null;
  }
}

/**
 * Props:
 * - title (string)           required
 * - content (html string)    required
 * - sectionSlug (string)     optional → shows breadcrumb back to /:sectionSlug
 * - updatedAt / createdAt    optional → shows a subtle date line
 */
export default function Pages({ title, content, sectionSlug, updatedAt, createdAt }) {
  const dateLabel = useMemo(
    () => fmtDate(updatedAt) || fmtDate(createdAt),
    [updatedAt, createdAt]
  );

  const contentRef = useRef(null);

  useEffect(() => {
    const root = contentRef.current;
    if (!root) return;

    const handleClick = async (e) => {
      // Bubblat klick → hitta närmaste <a>
      let el = e.target;
      while (el && el !== root && el.tagName !== "A") {
        el = el.parentElement;
      }
      if (!el || el === root || el.tagName !== "A") return;

      const a = el;
      const href = a.href;
      if (!href) return;

      // Bara intercepta GleSYS-länkar
      if (!href.includes("objects.dc-sto1.glesys.net")) return;

      // (valfritt) Om du bara vill ta PDF:er:
      // if (!href.toLowerCase().endsWith(".pdf")) return;

      const key = extractKeyFromS3Url(href);
      if (!key) return;

      e.preventDefault();

      try {
        const signedUrl = await getDownloadUrlForPrivate(key);
        window.open(signedUrl, "_blank", "noopener,noreferrer");
      } catch (err) {
        console.error("Failed to open private file", err);
        alert("Kunde inte öppna filen just nu.");
      }
    };

    root.addEventListener("click", handleClick);
    return () => root.removeEventListener("click", handleClick);
  }, []);

  return (
    <section>
      <div className="p-8 lg:py-16 lg:px-48">
        {sectionSlug && (
          <div className="mb-4">
            <Link to={`/${sectionSlug}`} className="text-sm text-blue-600 hover:underline">
              ← Tillbaka
            </Link>
          </div>
        )}

        <h2 className="text-2xl font-bold mb-2">{title}</h2>
        {dateLabel && <div className="text-sm text-gray-500 mb-4">{dateLabel}</div>}

        <div
          ref={contentRef}
          className="prose max-w-none"
          dangerouslySetInnerHTML={{
            __html: content && content.trim()
              ? content
              : "<p><em>Inget innehåll tillgängligt.</em></p>",
          }}
        />
      </div>
    </section>
  );
}
